import { Button, CompilerContent } from '@ws/shared/components';
import { Close16 } from '@ws/shared/icons';
import { TNote } from '@ws/shared/types';
import { compileNote } from '@ws/shared/utils';
import { classes } from 'classifizer';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useMount } from 'react-use';
import { debounce } from 'throttle-debounce';

import styles from './ContentWindow.module.css';

interface ContentWindowProps {
  note: TNote;
  scrollPercent: number;
  onScrollStop(scrollPercent: number): void;
  onCollapseClick(): void;
  onCloseClick?(): void;
}

export function ContentWindow({
  note,
  scrollPercent,
  onScrollStop,
  onCollapseClick,
  onCloseClick,
}: ContentWindowProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // latest — то, что пользователь видит прямо сейчас; committed — то, что уже ушло
  // наверх. Расхождение между ними и есть «не отправленный» скролл, который иначе
  // пропал бы при размонтировании раньше окна debounce'а.
  const latestPercentRef = useRef(scrollPercent);
  const committedPercentRef = useRef(scrollPercent);
  const onScrollStopRef = useRef(onScrollStop);
  onScrollStopRef.current = onScrollStop;

  function handleCollapseClick() {
    onCollapseClick();
  }

  useMount(() => {
    if (contentRef.current) {
      const scrollTop = contentRef.current.scrollHeight * scrollPercent;
      contentRef.current.scrollTop = scrollTop;
    }
  });

  useLayoutEffect(() => {
    const { current } = contentRef;

    if (!current) {
      return undefined;
    }

    // Свой debounce на каждый прогон эффекта, а НЕ один на всю жизнь компонента.
    // В throttle-debounce@3 cancel() выключает функцию навсегда (cancelled = true),
    // а StrictMode в dev прогоняет эффект как setup → cleanup → setup. Общий debounce
    // погибал бы на первом же реплее, и скролл открытого окна не коммитился бы вовсе.
    const commitScroll = debounce(300, (percent: number) => {
      committedPercentRef.current = percent;
      onScrollStopRef.current(percent);
    });

    const handleScroll = (event: HTMLElementEventMap['scroll']) => {
      if (!event.target) {
        return;
      }

      const element = event.target as HTMLDivElement;

      const height = element.scrollHeight;
      const top = element.scrollTop;

      // Пустой контейнер дал бы NaN, а он утечёт в стор и дальше в localStorage.
      if (!height) {
        return;
      }

      const percent = top / height;

      latestPercentRef.current = percent;
      commitScroll(percent);
    };

    current.addEventListener('scroll', handleScroll);

    return () => {
      current.removeEventListener('scroll', handleScroll);
      commitScroll.cancel();

      // debounce умеет cancel(), но не flush(), поэтому досылаем последнюю позицию
      // руками — иначе «проскроллил и сразу свернул окно» теряет скролл. После
      // досылки выравниваем committed, чтобы повторный cleanup не отправил её дважды.
      if (latestPercentRef.current !== committedPercentRef.current) {
        committedPercentRef.current = latestPercentRef.current;
        onScrollStopRef.current(latestPercentRef.current);
      }
    };
  }, []);

  const compiledContent = useMemo(() => {
    return compileNote(note);
  }, [note]);

  return (
    <div className={styles['window']}>
      <div className={styles['window__header']}>
        <div className={styles['window__header-title']}>{compiledContent.originalName}</div>
        <Button
          className={classes(styles['window__button'], styles['window__collapse-button'])}
          variant="shapeless"
          onClick={handleCollapseClick}
          icon={<div className={styles['window__collapse-icon']} />}
        />
        <Button
          className={classes(styles['window__button'], styles['window__close-button'])}
          variant="shapeless"
          onClick={onCloseClick}
          icon={<Close16 />}
        />
      </div>

      <div ref={contentRef} className={styles['window__content']}>
        <CompilerContent entity={compiledContent} />
      </div>
    </div>
  );
}
